import { useCallback, useMemo, useRef, useState } from 'react'

import CloseIcon from '@mui/icons-material/Close'
import { Box, Button, Grid, IconButton } from '@mui/material'
import { GridEventListener, GridSortItem } from '@mui/x-data-grid'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import { useDebounce } from 'use-debounce'

import { ControlledTextField, FplDataGrid } from 'components/common'
import { DEBOUNCE_DELAY } from 'constants/index'
import { useDeleteVehicleMutation, VehicleFilterInput } from 'generated/graphql'
import { GET_PAGED_VEHICLES } from 'graphql/queries'
import useGetVehiclesColumns from 'hooks/fplDataGridColumns/useGetVehiclesColumns'

import { CreateUpdateVehicle } from '../CreateUpdateVehicle'
import { DeleteVehicleDialog } from './DeleteVehicleDialog'

interface IProps {
  driverId: number | null
}

const Vehicles = (props: IProps) => {
  const { driverId } = props
  const fplDataGridRef = useRef<any>()
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    vehicleId: '',
  })

  const [dialog, setDialog] = useState<{ open: boolean; id: number | null }>({
    id: null,
    open: false,
  })

  const { control, watch, setValue } = useForm({
    shouldUnregister: true,
    defaultValues: {
      search: '',
    },
  })

  const [searchInput] = useDebounce(watch('search', ''), DEBOUNCE_DELAY)
  const searchValue: string = searchInput.length >= 3 ? searchInput : ''

  const [deleteVehicle, { loading: deletingVehicle }] = useDeleteVehicleMutation({
    onCompleted: (response) => {
      if (response.deleteVehicle) {
        toast.success('Vehicle was successfully deleted.')

        setDeleteDialog((prev) => ({
          ...prev,
          open: false,
        }))
        fplDataGridRef.current.queryRefetchOnTableUpdate()
      } else {
        toast.error('Something went wrong, please try again later.')
      }
    },
  })

  const handleDeleteVehicle = useCallback(
    (id: string) => {
      setDeleteDialog({
        open: true,
        vehicleId: id,
      })
    },
    [setDeleteDialog],
  )

  const columns = useGetVehiclesColumns({
    deletingVehicle,

    handleDeleteVehicle,
  })

  const handleRowClick: GridEventListener<'rowClick'> = ({ id }) => {
    setDialog({
      id: Number(id),
      open: true,
    })
  }

  const handleCreateVehicle = () => {
    setDialog({
      id: null,
      open: true,
    })
  }

  const handleSearchClearClick = () => {
    setValue('search', '')
  }

  const handleCloseVehicleDialog = () => {
    setDialog({
      id: null,
      open: false,
    })
  }

  const handleCloseDeleteDialog = () => {
    setDeleteDialog({
      open: false,
      vehicleId: '',
    })
  }

  const handleDeleteVehicleClick = () => {
    deleteVehicle({
      variables: {
        vehicleId: deleteDialog.vehicleId,
      },
    })
  }

  const filter: VehicleFilterInput = useMemo(() => {
    return {
      or: [{ registrationNumber: { contains: searchValue } }],
    }
  }, [searchValue])

  const queryData = useMemo(
    () => ({
      query: GET_PAGED_VEHICLES,
      entityName: 'vehicles',
      defaultSortOrder: {
        field: 'id',
        sort: 'asc',
      } as GridSortItem,
      queryVariables: { driverId: driverId },
    }),
    [driverId],
  )

  return (
    <>
      <FplDataGrid
        ref={fplDataGridRef}
        query={queryData.query}
        queryVariables={queryData.queryVariables}
        skipQuery={!driverId}
        entityName={queryData.entityName}
        columns={columns}
        filter={filter}
        defaultOrder={queryData.defaultSortOrder}
        toolbar={{
          refreshButton: true,
          caption: 'Vehicles',
          leftSide: (
            <Grid item>
              <Button variant='outlined' size='small' onClick={handleCreateVehicle}>
                New Vehicle
              </Button>
            </Grid>
          ),
          rightSide: (
            <Grid item>
              <Box width='210px'>
                <ControlledTextField
                  control={control}
                  name='search'
                  label='Search'
                  defaultValue=''
                  size='small'
                  endAdornment={
                    searchInput && (
                      <IconButton size='small' onClick={handleSearchClearClick}>
                        <CloseIcon fontSize='small' />
                      </IconButton>
                    )
                  }
                />
              </Box>
            </Grid>
          ),
        }}
        onRowClick={handleRowClick}
      />

      <CreateUpdateVehicle
        fplDataGridRef={fplDataGridRef}
        driverId={driverId as number}
        vehicleId={dialog.id}
        openDriverDialog={dialog.open}
        handleCloseDialog={handleCloseVehicleDialog}
      />

      {driverId && (
        <DeleteVehicleDialog
          driverId={driverId}
          vehicleId={Number(deleteDialog.vehicleId)}
          open={deleteDialog.open}
          onClose={handleCloseDeleteDialog}
          processing={deletingVehicle}
          onConfirm={handleDeleteVehicleClick}
        />
      )}
    </>
  )
}

export default Vehicles
